import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, CartUpdateEvent, DiscountUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * @typedef {Object} CartRemoveUndoPayload
 * @property {number} variantId
 * @property {number} quantity
 * @property {string} title
 * @property {Record<string, string>} [properties]
 * @property {number|null} [sellingPlan]
 * @property {number|null} [warrantyVariantId] - warranty removed together with the line (PP-987)
 * @property {string} [warrantySku]
 * @property {string} sectionId
 */

/** @type {CartRemoveUndoPayload | null} */
let pendingRemoveUndoPayload = null;

/**
 * Returns the warranty pair reference for a cart line.
 * Checks the current `_warranty_pair_id` first, then falls back to the legacy
 * `_warranty_ref` property so orphaned warranty lines from older sessions are
 * still detected and cleaned up.
 *
 * @param {object} item
 * @returns {string | null}
 */
function getWarrantyPairRef(item) {
  return item?.properties?._warranty_pair_id ?? item?.properties?._warranty_ref ?? null;
}

/**
 * Module-level orphan-cleanup state machine. Only one cleanup runs at a time;
 * triggers that arrive during a run set `warrantyCleanupRerun` so exactly one
 * follow-up pass is scheduled afterwards.
 */
let warrantyCleanupInFlight = false;
let warrantyCleanupRerun = false;
let warrantyCleanupScheduled = false;

/**
 * Serializes all cart mutations (quantity updates, removals, warranty coupling,
 * orphan cleanup). Concurrent requests raced each other before: a removal could
 * read a cart snapshot while a quantity update was still in flight, and the
 * later section morph of the earlier request overwrote the newer state in the
 * DOM. One chain means every task starts on the settled result of the previous
 * one. Errors are swallowed for the chain itself so one failed task never
 * blocks the queue.
 */
let cartMutationChain = Promise.resolve();

/**
 * Exported for modules that change the cart on their own (required add-ons,
 * PP-988) so their requests line up in the same queue.
 *
 * @param {() => Promise<unknown>} task
 * @returns {Promise<unknown>}
 */
export function enqueueCartMutation(task) {
  const run = cartMutationChain.then(task, task);
  cartMutationChain = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

/**
 * PP-987: line quantities on the page right before a removal is rendered, kept
 * per cart update event. A removal can merge lines (see #markMergedRows). Every
 * cart surface that renders the event compares against these quantities: the two
 * cart drawers of one header section render each other, so a surface that reads
 * its own rows when its turn comes may read them after the render already.
 * @type {WeakMap<Event, Map<string, number>>}
 */
const quantitiesBeforeRemoval = new WeakMap();

/**
 * Quantity of every cart line rendered on the page, by line item key.
 * @returns {Map<string, number>}
 */
function readRowQuantities() {
  /** @type {Map<string, number>} */
  const quantities = new Map();
  document
    .querySelectorAll('cart-items-component .cart-items__table-row[data-key]')
    .forEach((row) => {
      if (row instanceof HTMLElement && row.dataset.key && !quantities.has(row.dataset.key)) {
        quantities.set(row.dataset.key, Number(row.dataset.quantity) || 0);
      }
    });
  return quantities;
}

/**
 * Shopify Liquid serializes line item properties as an array; /cart/add.js expects a hash.
 *
 * @param {unknown} parsed
 * @returns {Record<string, string>}
 */
export function normalizeCartLineProperties(parsed) {
  /** @type {Record<string, string>} */
  const properties = {};

  if (!parsed) return properties;

  if (Array.isArray(parsed)) {
    for (const entry of parsed) {
      if (!entry || typeof entry !== 'object') continue;

      const record = /** @type {Record<string, unknown>} */ (entry);
      const key = record.key ?? record.name ?? record.first;
      const value = record.value ?? record.last;

      if (key == null || key === '' || String(key).charAt(0) === '_') continue;

      properties[String(key)] = value != null ? String(value) : '';
    }

    return properties;
  }

  if (typeof parsed === 'object') {
    for (const [key, value] of Object.entries(/** @type {Record<string, unknown>} */ (parsed))) {
      if (key.charAt(0) === '_' || value == null || value === '') continue;
      properties[key] = String(value);
    }
  }

  return properties;
}

/**
 * @param {HTMLElement} row
 * @param {string} sectionId
 * @returns {CartRemoveUndoPayload | null}
 */
export function getCartRemoveUndoPayloadFromRow(row, sectionId) {
  const variantId = Number(row.dataset.variantId);
  const quantity = Number(row.dataset.quantity);
  const title = row.dataset.productTitle ?? '';

  if (!variantId || !quantity) return null;

  /** @type {Record<string, string>} */
  let properties = {};

  if (row.dataset.properties) {
    try {
      properties = normalizeCartLineProperties(JSON.parse(row.dataset.properties));
    } catch {
      properties = {};
    }
  }

  const sellingPlanRaw = row.dataset.sellingPlan;
  const sellingPlan = sellingPlanRaw ? Number(sellingPlanRaw) : null;

  // PP-987: a product line with a warranty package loses the warranty line as
  // well, so the undo needs both (the package box carries the warranty data).
  const warrantyButton = row.querySelector('[data-warranty-variant-id]');
  const warrantyVariantId =
    Number(warrantyButton?.getAttribute('data-warranty-variant-id')) || null;
  const warrantySku = warrantyButton?.getAttribute('data-warranty-sku') ?? '';

  return {
    variantId,
    quantity,
    title,
    properties,
    sellingPlan: sellingPlan && !Number.isNaN(sellingPlan) ? sellingPlan : null,
    warrantyVariantId,
    warrantySku,
    sectionId,
  };
}

export function clearCartRemoveUndoQueue() {
  pendingRemoveUndoPayload = null;
}

/**
 * @param {CartRemoveUndoPayload} payload
 */
export function queueCartRemoveUndo(payload) {
  pendingRemoveUndoPayload = payload;
}

/**
 * @param {CartRemoveUndoPayload} payload
 */
export function presentCartRemoveUndo(payload) {
  pendingRemoveUndoPayload = payload;
  flushCartRemoveUndo();
}

/**
 * Shows the undo popup when the element is available (safe to call after morph).
 */
export function flushCartRemoveUndo() {
  if (!pendingRemoveUndoPayload) return;

  const undo = document.querySelector('cart-remove-undo');
  if (!undo || typeof (/** @type {any} */ (undo).show) !== 'function') return;

  if (undo.classList.contains('is-visible')) return;

  const payload = pendingRemoveUndoPayload;
  pendingRemoveUndoPayload = null;
  /** @type {any} */ (undo).show(payload);
}

/**
 * Schedules a debounced orphan cleanup pass. Multiple triggers collapse into one
 * run because `warrantyCleanupScheduled` is only cleared after the timeout fires.
 */
function scheduleWarrantyOrphanCleanup() {
  if (warrantyCleanupScheduled) return;
  warrantyCleanupScheduled = true;
  setTimeout(() => {
    warrantyCleanupScheduled = false;
    runWarrantyOrphanCleanup();
  }, 500);
}

/**
 * Removes orphaned warranty lines. The rendered cart marks them server-side
 * with `data-warranty-orphan` (see cart-products.liquid), so this only reads
 * the DOM — no extra cart request, no stale-snapshot race: a line only carries
 * the mark if it had no matching parent at render time, and removing such a
 * line is always correct. Keys are used for the update because line indices
 * shift. After the morph no marked row remains, so the pass terminates.
 */
async function runWarrantyOrphanCleanup() {
  if (warrantyCleanupInFlight) {
    warrantyCleanupRerun = true;
    return;
  }

  const orphanRows = document.querySelectorAll('[data-warranty-orphan][data-key]');
  if (!orphanRows.length) return;

  warrantyCleanupInFlight = true;

  try {
    // Runs inside the mutation queue; the orphan rows are re-read there so the
    // decision is based on the DOM state AFTER all previously queued mutations
    // (and their morphs) have settled.
    await enqueueCartMutation(async () => {
      const updates = {};
      const sectionIds = new Set();

      /** @type {HTMLElement[]} */
      const rows = [];
      document.querySelectorAll('[data-warranty-orphan][data-key]').forEach((row) => {
        if (row instanceof HTMLElement && row.dataset.key) {
          updates[row.dataset.key] = 0;
          rows.push(row);
        }
      });
      if (!Object.keys(updates).length) return;

      // PP-1019: warranty lines of a private customer's cart (marked by the cart
      // render with the reason and the pair token). Their product lines keep
      // warrantySKU and the pair token, and the order export reads that SKU: those
      // markers are cleared first, line by line through change.js, the only endpoint
      // that writes line properties. The warranty keys of the removal below do not
      // change through that. A failed marker cleanup is logged; the warranty line is
      // removed regardless, and the next pairing repair clears the markers.
      const privateRows = rows.filter((row) => row.dataset.warrantyOrphanReason === 'b2c');
      const privateRefs = new Set(
        privateRows.map((row) => row.dataset.warrantyPairRef).filter((ref) => ref)
      );
      const privateUnits = privateRows.reduce(
        (sum, row) => sum + (Number(row.dataset.quantity) || 1),
        0
      );
      if (privateRefs.size) {
        try {
          const cart = await fetch('/cart.js', { cache: 'no-store' }).then((res) => res.json());
          for (const item of cart.items ?? []) {
            // Lines scheduled for removal keep their key: a rewrite would change it
            // and the removal below would miss the line.
            if (item.key in updates || item.properties?._warranty === 'true') continue;
            if (!privateRefs.has(getWarrantyPairRef(item))) continue;
            const properties = { ...item.properties };
            delete properties.warrantySKU;
            delete properties._warranty_pair_id;
            delete properties._warranty_ref;
            // The cart ignores a properties update with an empty object, so a line
            // without any other property keeps a blank hidden token instead.
            const cleanedProperties = Object.keys(properties).length
              ? properties
              : { _warranty_pair_id: '' };
            const response = await fetch(Theme.routes.cart_change_url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: item.key,
                quantity: item.quantity,
                properties: cleanedProperties,
              }),
            });
            if (!response.ok) {
              throw new Error(`Warranty marker cleanup failed: HTTP ${response.status}`);
            }
          }
        } catch (error) {
          console.error('Warranty marker cleanup failed:', error);
        }
      }

      document.querySelectorAll('cart-items-component').forEach((component) => {
        if (component instanceof HTMLElement && component.dataset.sectionId) {
          sectionIds.add(component.dataset.sectionId);
        }
      });
      // The header section hosts the cart icon with count and subtotal.
      const headerSection = document.querySelector('cart-icon')?.closest('.shopify-section');
      if (headerSection?.id) {
        sectionIds.add(headerSection.id.replace('shopify-section-', ''));
      }

      // PP-1011: same request shape as the quantity updates (fetchConfig sets the
      // JSON accept header). Without it the update route answers with the cart page
      // instead of JSON, the line is removed on the server, but the cart on screen
      // keeps showing the removed warranty at 0.00 until the next reload.
      const response = await fetch(
        Theme.routes.cart_update_url,
        fetchConfig('json', {
          body: JSON.stringify({
            updates,
            sections: Array.from(sectionIds).join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      if (!response.ok) throw new Error(`Cart update failed with status ${response.status}`);
      const parsedResponse = await response.json();

      document.dispatchEvent(
        new CartUpdateEvent(parsedResponse, 'warranty-orphan-cleanup', {
          itemCount: parsedResponse.item_count ?? 0,
          source: 'warranty-orphan-cleanup',
          sections: parsedResponse.sections,
        })
      );

      // PP-1019: the customer is told that the warranty left the cart (notice of
      // client-type-selector.js, same as after a switch to private customer).
      if (privateUnits > 0) {
        const Selector = /** @type {any} */ (customElements.get('client-type-selector'));
        Selector?.announceWarrantyRemoval?.(privateUnits);
      }
    });
  } catch (error) {
    console.error('Warranty orphan cleanup failed:', error);
  } finally {
    warrantyCleanupInFlight = false;
    if (warrantyCleanupRerun) {
      warrantyCleanupRerun = false;
      runWarrantyOrphanCleanup();
    }
  }
}

/**
 * Re-syncs every cart surface with the real cart after the page comes back from
 * a back/forward navigation (PP-920 QA). Returning from the checkout that way
 * can show a stale cart: the browser restores the old DOM from the back/forward
 * cache, and a fresh request may even be answered from a server-side page cache
 * with markup rendered for an older cart state. Interacting with such stale
 * rows then mutates the wrong lines of the real cart. The dispatched cart
 * update event carries the live item count for the header bubble and no section
 * markup, so every cart-items component re-renders itself freshly from the
 * server (see #handleCartUpdate).
 */
function refreshCartAfterRestore() {
  fetch('/cart.js')
    .then((response) => (response.ok ? response.json() : null))
    .then((cart) => {
      if (!cart) return;
      document.dispatchEvent(
        new CartUpdateEvent({}, 'cart-restore', {
          source: 'cart-restore',
          itemCount: cart.item_count,
        })
      );
    })
    .catch(() => {});
}

// Fresh page load of a back/forward navigation (stale markup possible).
if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
  refreshCartAfterRestore();
}
// Instant restore from the back/forward cache (old DOM, no page load).
window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshCartAfterRestore();
});

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // Run an orphan cleanup once after mount. The schedule guard ensures the
    // drawer and page instances do not both start their own run.
    scheduleWarrantyOrphanCleanup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  async #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    // The rendered line index goes stale as soon as the cart changed after the
    // last render (warranty coupling and the orphan cleanup remove lines in the
    // background). The row's line item key stays stable, so it is resolved from
    // the event target and preferred for the update request.
    const rowKey =
      event.target instanceof Element
        ? event.target.closest('[data-key]')?.getAttribute('data-key') || undefined
        : undefined;

    if (quantity === 0) {
      clearCartRemoveUndoQueue();
      // Warranty coupling before the removal; if the lookup fails the removal
      // still runs — a leftover warranty line is caught by the orphan cleanup
      // afterwards. Queued so it never interleaves with other cart mutations.
      return enqueueCartMutation(async () => {
        let removedItem;
        /** @type {Record<string, number>} */
        let linkedUpdates = {};
        try {
          const cart = await fetch('/cart.js').then((res) => res.json());
          // The line-number fallback is only safe while the rendered row matches
          // the real cart. A keyed row that is missing from the live cart is a
          // stale rendering (page restored after checkout, PP-920 QA) — removing
          // by line number would hit an unrelated line, so re-render instead.
          removedItem = rowKey
            ? cart.items.find((item) => item.key === rowKey)
            : cart.items[line - 1];
          if (rowKey && !removedItem) {
            sectionRenderer.renderSection(this.sectionId, { cache: false });
            return;
          }
          linkedUpdates = await this.#applyWarrantyCoupling(cart, removedItem);
        } catch (error) {
          console.error('Warranty coupling on quantity zero failed:', error);
        }
        return this.updateQuantity(
          this.#removalConfig({
            line,
            key: removedItem?.key ?? rowKey,
            linkedUpdates,
            action: 'change',
          })
        );
      });
    }

    // Quantity changes run through the queue as well. A parent line with a
    // linked warranty updates BOTH lines in one atomic /cart/update.js call so
    // the warranty quantity always mirrors the parent quantity (SAP gets a
    // plain quantity times unit fee position, PP-862).
    enqueueCartMutation(async () => {
      try {
        const cart = await fetch('/cart.js').then((res) => res.json());
        // Same stale-row protection as the removal path above.
        const changedItem = rowKey
          ? cart.items.find((item) => item.key === rowKey)
          : cart.items[line - 1];
        if (rowKey && !changedItem) {
          sectionRenderer.renderSection(this.sectionId, { cache: false });
          return;
        }

        if (changedItem && changedItem.properties?._warranty !== 'true') {
          const pairRef = getWarrantyPairRef(changedItem);
          const warrantyItem = pairRef
            ? cart.items.find(
                (item) =>
                  item.properties?._warranty === 'true' && getWarrantyPairRef(item) === pairRef
              )
            : null;
          if (warrantyItem) {
            return this.updateQuantity({
              line,
              updates: {
                [changedItem.key]: quantity,
                [warrantyItem.key]: quantity,
              },
              quantity,
              action: 'change',
            });
          }
        }

        return this.updateQuantity({
          line,
          key: changedItem?.key ?? rowKey,
          quantity,
          action: 'change',
        });
      } catch (error) {
        console.error('Cart quantity update failed, falling back to plain update:', error);
        return this.updateQuantity({ line, key: rowKey, quantity, action: 'change' });
      }
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector('text-component')
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} [event] - The click event (passed by the declarative handler).
   */
  async onLineItemRemove(line, event) {
    // Resolve the intended line at CLICK time from the clicked element itself:
    // the warranty package button carries an explicit data-line-key (it sits
    // inside the PARENT row), normal remove buttons resolve via their own row.
    // The rendered line index is only the last fallback — it goes stale after
    // background cart changes.
    const clickedElement =
      event?.target instanceof Element ? event.target : /** @type {Element | null} */ (null);
    const clickedButton = clickedElement?.closest('button') ?? null;
    const explicitKey = clickedButton?.dataset.lineKey;
    // PP-987: rows are table rows in the default layout and articles in the
    // alternative cart page layout, so the row is resolved by its key attribute.
    const clickedRow = clickedButton?.closest('[data-key]') ?? null;
    const clickedRowKey = clickedRow?.getAttribute('data-key');
    const rowKey = explicitKey ?? clickedRowKey ?? this.refs.cartItemRows[line - 1]?.dataset.key;

    // Optimistically hide the warranty package box when its remove button was
    // clicked — the box lives inside the parent row, so the row animation below
    // would not give any feedback for it.
    if (explicitKey) {
      const packageBox = clickedButton?.closest(
        '.cart-items__warranty_products, .cart-alt-item__warranty'
      );
      if (packageBox instanceof HTMLElement) {
        // Collapsed, not just invisible: the line shrinks right away instead of
        // keeping the empty space until the cart renders again.
        packageBox.style.display = 'none';
        packageBox.closest('.cart-alt-item')?.classList.remove('cart-alt-item--has-warranty');
      }
    }

    enqueueCartMutation(async () => {
      let removedItem;
      /** @type {Record<string, number>} */
      let linkedUpdates = {};
      try {
        const cart = await fetch('/cart.js').then((res) => res.json());
        removedItem =
          (rowKey && cart.items.find((item) => item.key === rowKey)) || cart.items[line - 1];
        linkedUpdates = await this.#applyWarrantyCoupling(cart, removedItem);
      } catch (error) {
        console.error('Warranty coupling on line removal failed:', error);
      }
      // Removal via key (the index could have shifted by the marker cleanup
      // above); renders the sections so the UI reflects the full result.
      return this.updateQuantity(
        this.#removalConfig({
          line,
          key: removedItem?.key ?? rowKey,
          linkedUpdates,
          action: 'clear',
        })
      );
    });

    const cartItemRowToRemove = explicitKey
      ? (this.refs.cartItemRows.find((row) => row.dataset.key === explicitKey) ??
        this.refs.cartItemRows[line - 1])
      : ((clickedRow instanceof HTMLElement ? clickedRow : null) ??
        this.refs.cartItemRows[line - 1]);
    const usesRemoveUndo = this.classList.contains('cart-items-component__remove-undo');
    const usesAlternativeRemoveUndo = this.classList.contains('cart-items-component__alternative');
    // PP-987: no undo for warranty lines. The undo re-adds a line without its
    // hidden pairing properties, which would bring the warranty back as a
    // separate, unpriced cart line.
    const isWarrantyLine = cartItemRowToRemove?.dataset.warrantyLine === 'true';
    if ((usesAlternativeRemoveUndo || usesRemoveUndo) && cartItemRowToRemove && !isWarrantyLine) {
      const undoPayload = getCartRemoveUndoPayloadFromRow(cartItemRowToRemove, this.sectionId);
      if (undoPayload) {
        if (usesRemoveUndo) {
          queueCartRemoveUndo(undoPayload);
        } else {
          presentCartRemoveUndo(undoPayload);
        }
      }
    }
    if (!cartItemRowToRemove) return;
    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter(
        (row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key
      ),
    ];
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;
    const template = document.getElementById('empty-cart-template');
    const skipOptimisticEmptyState =
      this.classList.contains('cart-items-component__alternative') ||
      this.classList.contains('cart-items-component__remove-undo');

    if (isEmptyCart && template instanceof HTMLTemplateElement && !skipOptimisticEmptyState) {
      const clone = document.importNode(template.content, true);
      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);
      return;
    }

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();
      if (prefersReducedMotion()) return remove();
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Request config for a line removal. A product line with a linked warranty
   * removes both lines in ONE atomic /cart/update.js call (PP-987): the cart never
   * holds the orphaned warranty in between and the surfaces render once.
   * @param {{ line: number, key?: string, linkedUpdates: Record<string, number>, action: string }} config
   */
  #removalConfig({ line, key, linkedUpdates, action }) {
    if (key && Object.keys(linkedUpdates).length) {
      return { line, updates: { ...linkedUpdates, [key]: 0 }, quantity: 0, action };
    }
    return { line, key, quantity: 0, action };
  }

  /**
   * Warranty coupling for a removed line. A removed product line takes its linked
   * warranty line with it: that removal is RETURNED so the caller sends both
   * removals in one request (PP-987). A removed warranty line clears the markers
   * on its product line first; that needs change.js, the only endpoint that
   * writes line properties. All updates are key-based because line indices shift
   * after the first removal.
   *
   * @param {object} cart
   * @param {object | undefined} removedItem
   * @returns {Promise<Record<string, number>>} further line updates for the removal request
   */
  async #applyWarrantyCoupling(cart, removedItem) {
    if (!removedItem) return {};

    const isWarrantyLine = removedItem.properties?._warranty === 'true';
    const warrantyRef = getWarrantyPairRef(removedItem);

    if (!isWarrantyLine && warrantyRef) {
      const warrantyItem = cart.items.find(
        (item) => item.properties?._warranty === 'true' && getWarrantyPairRef(item) === warrantyRef
      );
      return warrantyItem ? { [warrantyItem.key]: 0 } : {};
    }

    if (isWarrantyLine && warrantyRef) {
      const parentItem = cart.items.find(
        (item) => item.properties?._warranty !== 'true' && getWarrantyPairRef(item) === warrantyRef
      );
      if (parentItem) {
        const properties = { ...parentItem.properties };
        delete properties.warrantySKU;
        delete properties._warranty_pair_id;
        delete properties._warranty_ref;
        // The cart ignores a properties update with an empty object, so a line
        // without any other property keeps a blank hidden token instead: that
        // still replaces the whole properties object and drops the SKU marker
        // (same as withoutWarrantyMarkers in warranty-addons.js).
        const cleanedProperties = Object.keys(properties).length
          ? properties
          : { _warranty_pair_id: '' };
        const response = await fetch(Theme.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: parentItem.key,
            quantity: parentItem.quantity,
            properties: cleanedProperties,
          }),
        });
        // Surfaces non-2xx to the caller's catch: stale warranty markers on the
        // parent are re-paired or cleaned up on the next cart interaction, but
        // the failure gets logged.
        if (!response.ok) {
          throw new Error(`Warranty marker cleanup failed: HTTP ${response.status}`);
        }
      }
    }

    return {};
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.key] - The line item key; preferred over the index when
   *   other lines were removed in the same interaction (indices shift, keys do not).
   * @param {Record<string, number>} [config.updates] - Multiple line item keys with
   *   their new quantities; posted as ONE atomic /cart/update.js call (used to keep
   *   a warranty line in sync with its parent). Takes precedence over key/line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @returns {Promise<void>} resolves when the response is processed (queue support).
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`
    );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      ...(config.updates
        ? { updates: config.updates }
        : {
            // change.js accepts the stable line item key as `id` — used when provided.
            ...(config.key ? { id: config.key } : { line: line }),
            quantity: quantity,
          }),
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });
    const endpoint = config.updates ? Theme.routes.cart_update_url : Theme.routes.cart_change_url;

    cartTotal?.shimmer();

    return fetch(`${endpoint}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors || !parsedResponseText.sections) {
          clearCartRemoveUndoQueue();
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount =
          newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        const cartUpdate = new CartUpdateEvent(parsedResponseText, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        });

        // A removal can merge lines (see #markMergedRows); a quantity input of the
        // customer changes the quantity on purpose and gets no highlight. The
        // quantities are read before the event goes out: the other cart surfaces
        // render the response right inside the dispatch.
        const quantitiesBefore = config.action === 'clear' ? readRowQuantities() : null;
        if (quantitiesBefore) quantitiesBeforeRemoval.set(cartUpdate, quantitiesBefore);

        this.dispatchEvent(cartUpdate);

        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId],
          this.isDrawer ? 'hydration' : 'full'
        ).then(() => {
          if (quantitiesBefore) this.#markMergedRows(quantitiesBefore);
          if (config.action === 'clear') {
            flushCartRemoveUndo();
          }
        });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const isOwnCleanup = event.detail?.data?.source === 'warranty-orphan-cleanup';

    // Do not trigger another cleanup while processing our own cleanup event,
    // but foreign cart updates may leave warranty orphans behind.
    if (!isOwnCleanup) {
      scheduleWarrantyOrphanCleanup();
    }

    const cartItemsHtml = event.detail?.data?.sections?.[this.sectionId];
    if (cartItemsHtml) {
      const quantitiesBefore = quantitiesBeforeRemoval.get(event);
      morphSection(this.sectionId, cartItemsHtml, this.isDrawer ? 'hydration' : 'full').then(() => {
        if (quantitiesBefore) this.#markMergedRows(quantitiesBefore);
      });

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * PP-987: a line that holds more units after a removal than before took over
   * the units of another line (the cart merges lines with the same product and
   * properties, e.g. once a warranty is removed from one of them). A short
   * highlight shows where the units went.
   * @param {Map<string, number>} quantitiesBefore - see quantitiesBeforeRemoval
   */
  #markMergedRows(quantitiesBefore) {
    this.querySelectorAll('.cart-items__table-row[data-key]').forEach((row) => {
      if (!(row instanceof HTMLElement) || !row.dataset.key) return;
      const before = quantitiesBefore.get(row.dataset.key);
      if (before === undefined || (Number(row.dataset.quantity) || 0) <= before) return;
      row.classList.add('cart-items__table-row--merged');
      onAnimationEnd(row, () => row.classList.remove('cart-items__table-row--merged'));
    });
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
